/**
 * HealthSummaryCard Component
 * Displays OKR health summary metrics
 *
 * Story 4.5: Health Summary
 */

import React from 'react';
import { clsx } from 'clsx';
import {
  Activity,
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  Clock,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';
import { Card } from '../../common';
import { OrgBaselineNote } from '../ReportScopeIndicator';
import type { OKRHealthSummary } from '../../../services/reportApi';
import type { ResolvedScope } from '../../../hooks/useReportData';

export interface HealthSummaryCardProps {
  /** Health summary data */
  data: OKRHealthSummary;
  /** Loading state */
  isLoading?: boolean;
  /**
   * Resolved scope for these figures, so the zero state can say WHY it is
   * empty. Resolved by the hook layer — this card never reads `data.scope`.
   */
  scope?: ResolvedScope;
  /** Additional class names */
  className?: string;
}

/**
 * Single metric card
 */
interface MetricProps {
  icon: React.ComponentType<{ className?: string }>;
  iconColor: string;
  label: string;
  value: number | string;
  subtext?: string;
}

const Metric: React.FC<MetricProps> = ({ icon: Icon, iconColor, label, value, subtext }) => (
  <div className="text-center p-4 bg-stone-50 rounded-lg">
    <div className={clsx('inline-flex p-2 rounded-full mb-2', iconColor.replace('text-', 'bg-').replace('600', '100'))}>
      <Icon className={clsx('w-5 h-5', iconColor)} />
    </div>
    <div className="text-2xl font-bold text-stone-900">{value}</div>
    <div className="text-xs text-stone-500">{label}</div>
    {subtext && <div className="text-xs text-stone-400 mt-1">{subtext}</div>}
  </div>
);

/**
 * Loading skeleton
 */
const SummarySkeleton: React.FC = () => (
  <div className="animate-pulse">
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="p-4 bg-stone-100 rounded-lg">
          <div className="w-9 h-9 bg-stone-200 rounded-full mx-auto mb-2" />
          <div className="h-8 bg-stone-200 rounded w-16 mx-auto mb-2" />
          <div className="h-4 bg-stone-200 rounded w-12 mx-auto" />
        </div>
      ))}
    </div>
    <div className="mt-4 p-4 bg-stone-100 rounded-lg">
      <div className="h-4 bg-stone-200 rounded w-full mb-2" />
      <div className="h-2 bg-stone-200 rounded w-full" />
    </div>
  </div>
);

/**
 * Health Summary Card Component
 */
export const HealthSummaryCard: React.FC<HealthSummaryCardProps> = ({
  data,
  isLoading = false,
  scope,
  className,
}) => {
  if (isLoading) {
    return (
      <Card className={clsx('p-6', className)}>
        <div className="flex items-center gap-2 mb-4">
          <Activity className="w-5 h-5 text-indigo-600" />
          <h3 className="text-lg font-semibold text-stone-900">Health Overview</h3>
        </div>
        <SummarySkeleton />
      </Card>
    );
  }

  const {
    totalOKRs,
    healthyCount,
    atRiskCount,
    criticalCount,
    stagnantCount,
    avgProgress,
    expectedProgress,
    completionPrediction,
  } = data;

  const progressDiff = avgProgress - expectedProgress;
  const isOnTrack = progressDiff >= 0;

  // Calculate health percentage
  const healthPercentage = totalOKRs > 0 ? Math.round((healthyCount / totalOKRs) * 100) : 0;

  // No OKRs in scope: every figure below is a true zero over an empty set, but
  // reads as "your team is failing" — a 0% health bar and "-42.0% vs expected"
  // where the honest answer is "there is nothing here to measure yet".
  if (totalOKRs === 0) {
    return (
      <Card className={clsx('p-6', className)}>
        <div className="flex items-center gap-2 mb-4">
          <Activity className="w-5 h-5 text-indigo-600" />
          <h3 className="text-lg font-semibold text-stone-900">Health Overview</h3>
        </div>
        <div className="text-center py-8 text-stone-500">
          <Activity className="w-12 h-12 mx-auto mb-3 text-stone-300" />
          <p>
            {scope?.isSelfOnly
              ? 'You own no OKRs in this period'
              : scope?.kind === 'team'
                ? 'Your team owns no OKRs in this period'
                : 'No OKRs in this period'}
          </p>
          {scope?.isSelfOnly && (
            <p className="mt-1 text-xs text-stone-400">{scope.description}</p>
          )}
        </div>
        {data.orgBaseline && (
          <OrgBaselineNote
            className="justify-center"
            value={`${data.orgBaseline.totalOKRs}`}
            label="OKRs this period"
          />
        )}
      </Card>
    );
  }

  return (
    <Card className={clsx('p-6', className)}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-indigo-600" />
          <h3 className="text-lg font-semibold text-stone-900">Health Overview</h3>
        </div>
        <div className={clsx(
          'flex items-center gap-1 text-sm font-medium',
          isOnTrack ? 'text-green-600' : 'text-red-600'
        )}>
          {isOnTrack ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
          {progressDiff > 0 ? '+' : ''}{progressDiff.toFixed(1)}% vs expected
        </div>
      </div>

      {/* Metrics grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Metric
          icon={CheckCircle}
          iconColor="text-green-600"
          label="Healthy"
          value={healthyCount}
          subtext={`${healthPercentage}% of total`}
        />
        <Metric
          icon={AlertTriangle}
          iconColor="text-amber-600"
          label="At Risk"
          value={atRiskCount}
        />
        <Metric
          icon={AlertCircle}
          iconColor="text-red-600"
          label="Critical"
          value={criticalCount}
        />
        <Metric
          icon={Clock}
          iconColor="text-stone-600"
          label="Stagnant"
          value={stagnantCount}
        />
      </div>

      {/* Progress comparison */}
      <div className="mt-6 p-4 bg-stone-50 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-stone-700">Overall Progress</span>
          <span className="text-sm text-stone-600">
            <span className="font-medium">{avgProgress.toFixed(1)}%</span>
            <span className="text-stone-400 ml-1">/ {expectedProgress.toFixed(1)}% expected</span>
          </span>
        </div>
        {/* Progress bar */}
        <div className="relative h-3 bg-stone-200 rounded-full overflow-hidden">
          {/* Expected progress marker */}
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-indigo-600 z-10"
            style={{ left: `${Math.min(expectedProgress, 100)}%` }}
          />
          {/* Actual progress */}
          <div
            className={clsx(
              'h-full rounded-full transition-all',
              isOnTrack ? 'bg-green-500' : 'bg-amber-500'
            )}
            style={{ width: `${Math.min(avgProgress, 100)}%` }}
          />
        </div>
        <div className="flex items-center justify-between mt-2 text-xs text-stone-500">
          <span>0%</span>
          <span>Predicted completion: {completionPrediction}%</span>
          <span>100%</span>
        </div>
        {/* The context D-B1 promised in exchange for the narrower row lists.
            Absent for an unrestricted caller — for them the figure above
            already IS the organization. */}
        {data.orgBaseline && (
          <OrgBaselineNote
            className="mt-3"
            value={`${data.orgBaseline.avgProgress.toFixed(1)}%`}
            label={`avg progress across ${data.orgBaseline.totalOKRs} OKRs`}
          />
        )}
      </div>

      {/* Quick stats row */}
      <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-stone-200">
        <div className="text-center">
          <p className="text-lg font-bold text-stone-900">{totalOKRs}</p>
          <p className="text-xs text-stone-500">Total OKRs</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-bold text-stone-900">{avgProgress.toFixed(0)}%</p>
          <p className="text-xs text-stone-500">Avg Progress</p>
        </div>
        <div className="text-center">
          <p className="text-lg font-bold text-stone-900">{completionPrediction}%</p>
          <p className="text-xs text-stone-500">Est. Completion</p>
        </div>
      </div>
    </Card>
  );
};

export default HealthSummaryCard;
